import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSpeakerNotePages } from "../../../queries/speakerNotes";
import NotePageList from "../../../components/notes/NotePageList";
import NoteEditorPanel from "../../../components/notes/NoteEditorPanel";
import TipCallout from "../../../components/feedback/TipCallout";

/**
 * @description 발표자 노트 페이지
 * - 왼쪽: 노트 페이지 목록 (content 없이 가볍게 로드)
 * - 오른쪽: 선택된 페이지의 content (선택 시 로드)
 */
export default function SpeakerNotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get("id");

  const {
    pages,
    activePage,
    loading,
    contentLoading,
    selectPage,
    handleContentChange,
    handleTitleChange,
    addPage,
    removePage,
  } = useSpeakerNotePages(pdfId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              {t("dashboard.speaker_notes_title")}
            </h1>
          </div>
        </div>
        {/* 자동저장이므로 저장 버튼 제거 */}
        <span className="text-xs text-gray-400">자동 저장</span>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <NotePageList
            pages={pages}
            activePageId={activePage?.id ?? null}
            sectionLabel="페이지"
            addAriaLabel="새 페이지 추가"
            emptyMessage="+ 버튼으로 페이지를 추가하세요"
            onSelect={selectPage}
            onAdd={addPage}
            onRemove={removePage}
            onRename={handleTitleChange}
          />

          <div className="flex-1">
            <NoteEditorPanel
              contentLoading={contentLoading}
              activeTitle={activePage?.title ?? null}
              activeContent={activePage?.content ?? ""}
              emptyMessage="페이지를 선택하거나 추가하세요"
              placeholder={t("dashboard.notes_placeholder")}
              onChangeContent={handleContentChange}
            />

            <div className="mt-4">
              <TipCallout
                title={t("dashboard.notes_tip_title")}
                description={t("dashboard.notes_tip_desc")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
